/**
 * EmbeddedBoard — the REAL board rendered inline in a small box, rooted at one
 * module's subspace, reading the SHARED layout from the surrounding composition
 * (`LayoutProvider`). Many of these mount over ONE compiled model + one solve: the
 * host wraps a `ViewerProviders`/`LayoutProvider`, and each card projects a
 * different subspace of the one shared `root` — no per-card compile or solve.
 *
 * Composition, not a flag: an embed is a `BoardProvider` (its own per-board
 * runtime — viewport/camera) wrapping a bare `BoardSurface` with NO chrome. It
 * sheds the whole chrome subtree (DOM, listeners, ResizeObservers) by absence,
 * and `interactive={false}` keeps the wheel/drag/touch/keyboard hooks and the
 * out-of-bounds timer inert. Still the real board, projected over the shared model.
 */
import { useMemo, type ReactNode } from "react";
import { asSource } from "@archlang/scene/board";
import { BoardProvider } from "../board/BoardProvider.js";
import { BoardSurface } from "../board/BoardSurface.js";
import { useLayoutContextOptional } from "../workbench/LayoutContext.js";

export interface EmbeddedBoardProps {
  /** Focal path into the shared root — `[rootSpaceId, subspaceId, …]`. The host
   *  derives it from the shared root (a container item's `space.id`). */
  readonly focalPath: readonly string[];
}

export function EmbeddedBoard({ focalPath }: EmbeddedBoardProps): ReactNode {
  const layout = useLayoutContextOptional();
  // Project the subspace out of the ONE shared solved root (cheap: a cache hit in
  // the shared engine once solved). The resulting BoardSpace roots this board at
  // that subspace with a default fit viewport.
  const space = useMemo(() => {
    if (!layout?.root) return null;
    try {
      return asSource(layout.root).at(focalPath);
    } catch {
      return null;
    }
  }, [layout?.root, focalPath]);

  if (!space) return null;
  return (
    <BoardProvider root={space} operator={false}>
      {/* A preview, not a navigable board: no minimap/zoom HUD (it's non-
          interactive, so there's nothing to navigate — the HUD is pure clutter
          at card size). */}
      <BoardSurface interactive={false} hideMinimap />
    </BoardProvider>
  );
}
